import { useRef } from 'react';
import { Container, Nav, Card, Row, Col, Table, Alert, Button, Spinner, Badge } from 'react-bootstrap';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminService, HostSecurityCount, HostSecuritySummary } from '../services/admin.service';

const CHECK_IDS = [
  'ufw',
  'ssh_key_only',
  'ssh_no_root',
  'sshd_jail',
  'mail_jail',
  'probe_deny',
  'nginx_probe_jail',
  'recidive',
] as const;

function formatWhen(value: string | undefined, language: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function CountTable({ rows }: { rows: HostSecurityCount[] }) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return <p className="text-muted mb-0">{t('host_security.none', { defaultValue: '記録はありません。' })}</p>;
  }
  return (
    <Table size="sm" className="mb-0">
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td className="text-break">{row.key}</td>
            <td className="text-end text-nowrap">{row.count.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function CheckList({ data }: { data: HostSecuritySummary }) {
  const { t } = useTranslation();
  return (
    <ul className="mb-0">
      {CHECK_IDS.map((id) => {
        const check = data.checks.find((item) => item.id === id);
        const ok = check?.ok;
        const label = t(`host_security.check.${id}`, { defaultValue: id });
        if (ok === true) {
          return (
            <li key={id}>
              <Badge bg="success" className="me-2">{t('host_security.in_place', { defaultValue: '有効' })}</Badge>
              {label}
            </li>
          );
        }
        if (ok === false) {
          return (
            <li key={id}>
              <Badge bg="danger" className="me-2">{t('host_security.missing', { defaultValue: '不足' })}</Badge>
              {label}
            </li>
          );
        }
        return (
          <li key={id}>
            <Badge bg="secondary" className="me-2">{t('host_security.unknown', { defaultValue: '未確認' })}</Badge>
            {label}
          </li>
        );
      })}
    </ul>
  );
}

export function HostSecurityPanel() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const forceRefresh = useRef(false);
  const query = useQuery({
    queryKey: ['host-security'],
    queryFn: () => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      return adminService.getHostSecurity(refresh);
    },
    staleTime: 5 * 60 * 1000,
  });

  const data = query.data?.data;
  const gaps = data?.checks.filter((check) => check.ok === false) ?? [];
  const unknown = data?.checks.filter((check) => check.ok == null) ?? [];

  return (
    <Container className="pb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="text-muted mb-0">
          {t('host_security.defense_lead', {
            defaultValue: 'この画面を出しているサーバー自身の通信記録です。',
          })}
        </p>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={query.isFetching}
          onClick={() => {
            forceRefresh.current = true;
            void queryClient.invalidateQueries({ queryKey: ['host-security'] });
          }}
        >
          {query.isFetching
            ? t('host_security.loading', { defaultValue: '集計しています' })
            : t('host_security.refresh', { defaultValue: '読み直す' })}
        </Button>
      </div>

      {query.isLoading && (
        <div className="py-4 text-center">
          <Spinner animation="border" size="sm" role="status" />
        </div>
      )}
      {query.isError && (
        <Alert variant="danger">
          {t('host_security.load_error', { defaultValue: '通信記録を読み取れませんでした。' })}
        </Alert>
      )}

      {data && (
        <>
          <Alert variant={gaps.length > 0 ? 'warning' : 'success'}>
            <div>
              {t('host_security.host_line', {
                defaultValue: 'サーバー {{host}}。集計 {{when}}。',
                host: data.hostname,
                when: formatWhen(data.generatedAt, i18n.language),
              })}
            </div>
            <div className="mt-1">
              {gaps.length === 0 && unknown.length === 0
                ? t('host_security.verdict_ok', {
                    defaultValue: 'いま有効な防御は、この記録に対して足りています。',
                  })
                : t('host_security.verdict_gap', {
                    defaultValue: '足りない防御があります。下の一覧の「不足」が対象です。',
                  })}
            </div>
          </Alert>

          <Row className="g-3 mb-3">
            <Col lg={6}>
              <Card className="h-100">
                <Card.Header>{t('host_security.checks_title', { defaultValue: 'いま有効な防御' })}</Card.Header>
                <Card.Body>
                  <CheckList data={data} />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="h-100">
                <Card.Header>{t('host_security.window_title', { defaultValue: '記録が残っている期間' })}</Card.Header>
                <Card.Body>
                  <p className="mb-2">
                    SSH {data.windows.auth ? `${data.windows.auth.from} – ${data.windows.auth.to}` : t('host_security.none')}
                  </p>
                  <p className="mb-2">
                    {t('host_security.mail_title', { defaultValue: 'メール' })}{' '}
                    {data.windows.mail ? `${data.windows.mail.from} – ${data.windows.mail.to}` : t('host_security.none')}
                  </p>
                  <p className="mb-0">
                    Web {data.windows.web ? `${data.windows.web.from} – ${data.windows.web.to}` : t('host_security.none')}
                  </p>
                  {data.unavailable.length > 0 && (
                    <p className="text-muted small mt-3 mb-0">
                      {t('host_security.unreadable', { defaultValue: '読めなかったファイルがあります。' })}{' '}
                      {data.unavailable.join(', ')}
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mb-3">
            <Card.Header>SSH</Card.Header>
            <Card.Body>
              <p>
                {t('host_security.ssh_body', {
                  defaultValue:
                    '存在しない名前 {{invalid}} 件、パスワード失敗 {{failed}} 件、鍵を示す前に切れた接続 {{preauth}} 件。失敗の送信元は {{ips}}。',
                  invalid: data.ssh.invalidUserEvents.toLocaleString(),
                  failed: data.ssh.failedPasswordEvents.toLocaleString(),
                  preauth: data.ssh.preauthDisconnects.toLocaleString(),
                  ips: data.ssh.uniqueFailIps.toLocaleString(),
                })}
              </p>
              <p className="mb-2">
                {data.ssh.passwordAccepted === 0 && data.ssh.rootAccepted === 0
                  ? t('host_security.ssh_ok', {
                      defaultValue: 'この期間に、パスワードや root でのログイン成功はありません。',
                    })
                  : t('host_security.ssh_bad', {
                      defaultValue: 'パスワード成功 {{password}} 件、root 成功 {{root}} 件あります。',
                      password: data.ssh.passwordAccepted,
                      root: data.ssh.rootAccepted,
                    })}
              </p>
              <Row>
                <Col md={4}>
                  <h2 className="h6">{t('host_security.accepted', { defaultValue: 'ログインできたもの' })}</h2>
                  {data.ssh.accepted.length === 0 ? (
                    <p className="text-muted">{t('host_security.none')}</p>
                  ) : (
                    <ul className="mb-0">
                      {data.ssh.accepted.map((row) => (
                        <li key={`${row.user}-${row.method}`}>
                          {row.user} / {row.method} / {row.count.toLocaleString()} / {row.addresses.join(', ')}
                        </li>
                      ))}
                    </ul>
                  )}
                </Col>
                <Col md={4}>
                  <h2 className="h6">{t('host_security.top_names', { defaultValue: '多く試された名前' })}</h2>
                  <CountTable rows={data.ssh.topUsers} />
                </Col>
                <Col md={4}>
                  <h2 className="h6">{t('host_security.top_addresses', { defaultValue: '多い送信元' })}</h2>
                  <CountTable rows={data.ssh.topIps} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>{t('host_security.mail_title', { defaultValue: 'メール' })}</Card.Header>
            <Card.Body>
              <p>
                {t('host_security.mail_body', {
                  defaultValue:
                    '認証失敗 {{total}} 件。うち名前の総当たり {{guess}} 件、仕組みの不一致 {{mechanism}} 件、途中で切れたもの {{lost}} 件。',
                  total: data.mail.authFailures.toLocaleString(),
                  guess: data.mail.otherFailures.toLocaleString(),
                  mechanism: data.mail.invalidMechanism.toLocaleString(),
                  lost: data.mail.connectionLost.toLocaleString(),
                })}
              </p>
              <Row>
                <Col md={6}>
                  <h2 className="h6">{t('host_security.mail_ok', { defaultValue: '認証が通ったもの' })}</h2>
                  {data.mail.successes.length === 0 ? (
                    <p className="text-muted">{t('host_security.none')}</p>
                  ) : (
                    <ul className="mb-0">
                      {data.mail.successes.map((row) => (
                        <li key={`${row.user}-${row.address}`}>
                          {row.user} / {row.address || '—'} / {row.count.toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  )}
                </Col>
                <Col md={6}>
                  <h2 className="h6">{t('host_security.top_names')}</h2>
                  <CountTable rows={data.mail.topUsers} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>{t('host_security.web_title', { defaultValue: 'Web の探索' })}</Card.Header>
            <Card.Body>
              <p>
                {t('host_security.web_body', {
                  defaultValue:
                    '.env や .php、WordPress、cgi-bin などへのリクエスト {{hits}} 件、送信元 {{ips}}。状態 200 は {{shell}} 件で、これは設定ファイルではなく画面の HTML であることがあります。',
                  hits: data.web.probeRequests.toLocaleString(),
                  ips: data.web.uniqueIps.toLocaleString(),
                  shell: data.web.appShellResponses.toLocaleString(),
                })}
              </p>
              <Row>
                <Col md={4}>
                  <h2 className="h6">{t('host_security.by_status', { defaultValue: '応答' })}</h2>
                  <CountTable rows={data.web.byStatus} />
                </Col>
                <Col md={8}>
                  <h2 className="h6">{t('host_security.top_paths', { defaultValue: '多いパス' })}</h2>
                  <CountTable rows={data.web.topPaths} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>{t('host_security.ban_title', { defaultValue: '遮断（fail2ban）' })}</Card.Header>
            <Card.Body>
              <p>
                {t('host_security.ban_body', {
                  defaultValue: '遮断 {{bans}} 回、送信元 {{ips}}。',
                  bans: data.fail2ban.bans.toLocaleString(),
                  ips: data.fail2ban.uniqueIps.toLocaleString(),
                })}
              </p>
              <Row>
                <Col md={6}>
                  <h2 className="h6">{t('host_security.by_jail', { defaultValue: '種類' })}</h2>
                  <CountTable rows={data.fail2ban.byJail} />
                </Col>
                <Col md={6}>
                  <h2 className="h6">{t('host_security.top_addresses')}</h2>
                  <CountTable rows={data.fail2ban.topIps} />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}

export default function AdminInsightsLayout() {
  const { t } = useTranslation();
  return (
    <>
      <Container className="pt-4 pb-0">
        <h1 className="h3 mb-2">{t('host_security.page_title', { defaultValue: '利用と防御' })}</h1>
        <p className="text-muted">
          {t('host_security.page_lead', {
            defaultValue: 'サーバーに届いた通信と、サイト内のアクセス・転換・機能の利用をここで見ます。',
          })}
        </p>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link as={NavLink} to="/admin/insights/defense" end>
              {t('host_security.tab_defense', { defaultValue: '通信' })}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="/admin/insights/access-logs">
              {t('navigation.access_logs', { defaultValue: 'アクセスログ' })}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="/admin/insights/conversion">
              {t('navigation.conversion_funnel', { defaultValue: '転換ファネル' })}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="/admin/insights/user-activity">
              {t('navigation.user_activity', { defaultValue: 'ユーザー行動・機能利用' })}
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </Container>
      <Outlet />
    </>
  );
}
